import { ConstraintViolationError, Violation } from 'src/utils/ErrorDataHelper';

type BaseNamePath = string | number | boolean | (string | number | boolean)[];

type DeepNamePath<
    Store = any,
    ParentNamePath extends any[] = [],
> = ParentNamePath['length'] extends 10
    ? never
    : true extends (Store extends BaseNamePath ? true : false)
    ? ParentNamePath['length'] extends 0
        ? Store | BaseNamePath
        : Store extends any[]
        ? [...ParentNamePath, number]
        : never
    : Store extends any[] // Connect path. e.g. { a: { b: string }[] }
    ?
          | [...ParentNamePath, number]
          | DeepNamePath<Store[number], [...ParentNamePath, number]>
    : keyof Store extends never
    ? Store
    : {
          // eslint-disable-next-line @typescript-eslint/ban-types
          [FieldKey in keyof Store]: Store[FieldKey] extends Function
              ? never
              :
                    | (ParentNamePath['length'] extends 0 ? FieldKey : never)
                    | [...ParentNamePath, FieldKey]
                    | DeepNamePath<
                          Required<Store>[FieldKey],
                          [...ParentNamePath, FieldKey]
                      >;
      }[keyof Store];

export type NamePath<T = any> = DeepNamePath<T>;

export type ListFilter<T> = Omit<T, 'page' | 'size' | 'sort'>;

function namePathToArray(path?: NamePath): any[] {
    if (path === undefined || path === null) {
        return [];
    }

    return Array.isArray(path) ? path : [path];
}

function createNameProvider<T>() {
    return {
        /**
         * Used to specify the correct path to a property based on the passed data type.
         * Returns the path to the property as an array
         * @param values
         * @returns Returns the path to the property as an array
         */
        getPath: (values: NamePath<T>) => {
            const path = namePathToArray(values);

            return path as NamePath<T>;
        },
    };
}

export type FieldData = {
    name: string | Array<string | number>;
    errors: string[];
    touched: boolean;
};

const getFieldDataItems = (
    errorData?: ConstraintViolationError,
): FieldData[] => {
    const fieldViolationsMap = getViolationsMap(errorData?.violations || []);

    return Object.entries(fieldViolationsMap).map(([fieldName, violations]) => {
        return {
            name: convertStringToNamePath(normalizeFieldName(fieldName)),
            errors: violations.map((v) => v.message),
            touched: true,
        };
    });
};

const getViolationsMap = (
    violations: Violation[],
): Record<string, Violation[]> => {
    return (
        violations.reduce((acc, violation) => {
            if (!acc[violation.field]) {
                acc[violation.field] = [];
            }

            acc[violation.field].push(violation);

            return acc;
        }, {} as Record<string, Violation[]>) || {}
    );
};

const convertStringToNamePath = (fieldName: string) => {
    return fieldName
        .split('.')
        .filter((part) => Boolean(part))
        .map((part) => {
            if (Number.isFinite(Number.parseInt(part))) {
                return Number.parseInt(part);
            }

            return part;
        });
};

const CROSS_PARAMETER_PATTERN = /.*<cross-parameter>\./i;

// eslint-disable-next-line no-useless-escape
const ARRAY_PATTERN = /[\[\]]/gm;

const normalizeFieldName = (fieldName: string) => {
    return fieldName
        .replace(CROSS_PARAMETER_PATTERN, '')
        .replace(ARRAY_PATTERN, '.');
};

export const FormHelper = {
    getFieldDataItems,
    createNameProvider,
};
